import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/currencyUtils';
import type { CheckReportFirm } from '../../types/report';

/**
 * R17 Task H: one firm's aggregate card + expandable per-check detail
 * for the Check Report screen. Layout mirrors the Client Report's
 * per-client card so the two report screens read as one family.
 */
interface Props {
  firm: CheckReportFirm;
}

const CheckReportFirmCard: React.FC<Props> = ({ firm }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={() => setExpanded(prev => !prev)}
        className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex-1">
          <h3 className="text-base font-semibold text-gray-900 flex items-center">
            {firm.firm_name}
            {expanded ? (
              <ChevronUp className="h-5 w-5 ml-2 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 ml-2 text-gray-400" />
            )}
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1">
            <div>
              <span className="text-xs text-gray-500">Checks: </span>
              <span className="text-sm font-medium text-gray-900">{firm.check_count}</span>
            </div>
            <div>
              <span className="text-xs text-gray-500">Gross: </span>
              <span className="text-sm font-semibold text-green-700">{formatCurrency(firm.gross)}</span>
            </div>
            <div>
              <span className="text-xs text-gray-500">Salesman: </span>
              <span className="text-sm font-semibold text-blue-700">{formatCurrency(firm.salesman_commission)}</span>
            </div>
            <div>
              <span className="text-xs text-gray-500">House: </span>
              <span className="text-sm font-semibold text-amber-700">{formatCurrency(firm.house_commission)}</span>
            </div>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Date</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Salesman</th>
                <th className="px-3 py-2 text-right font-medium text-gray-500">Amount</th>
                <th className="px-3 py-2 text-right font-medium text-gray-500">Payout %</th>
                <th className="px-3 py-2 text-right font-medium text-gray-500">Salesman Comm</th>
                <th className="px-3 py-2 text-right font-medium text-gray-500">House Comm</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Memo</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {firm.checks.map(check => (
                <tr key={check.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-900 whitespace-nowrap">{formatDate(check.check_date)}</td>
                  <td className="px-3 py-2 text-gray-900">{check.salesman_name}</td>
                  <td className="px-3 py-2 text-right text-green-700 font-medium">{formatCurrency(check.amount)}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{check.payout_percentage.toFixed(2)}%</td>
                  <td className="px-3 py-2 text-right text-blue-700 font-medium">{formatCurrency(check.salesman_commission)}</td>
                  <td className="px-3 py-2 text-right text-amber-700 font-medium">{formatCurrency(check.house_commission)}</td>
                  <td className="px-3 py-2 text-gray-600 truncate max-w-xs">{check.memo || ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-100 font-semibold">
                <td className="px-3 py-2 text-right" colSpan={2}>Firm Total:</td>
                <td className="px-3 py-2 text-right text-green-700">{formatCurrency(firm.gross)}</td>
                <td></td>
                <td className="px-3 py-2 text-right text-blue-700">{formatCurrency(firm.salesman_commission)}</td>
                <td className="px-3 py-2 text-right text-amber-700">{formatCurrency(firm.house_commission)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default CheckReportFirmCard;
